<template>
    <div v-if="!!planningItem">
        <div class="dossier-section">
            <div class="dossier-header" @click="toggle('attachments')">
                <md-icon>attach_file</md-icon>
                <span class="dossier-title">Bijlagen ({{ attachmentCount }})</span>
                <md-icon>{{ isOpen('attachments') ? 'expand_less' : 'expand_more' }}</md-icon>
            </div>
            <div v-show="isOpen('attachments')" class="dossier-content">
                <planning-detail-attachments-view />
            </div>
        </div>

        <div v-if="showQuotes" class="dossier-section">
            <div class="dossier-header" @click="toggle('quotes')">
                <md-icon>request_quote</md-icon>
                <span class="dossier-title">Offertes ({{ quoteCount }})</span>
                <md-icon>{{ isOpen('quotes') ? 'expand_less' : 'expand_more' }}</md-icon>
            </div>
            <div v-show="isOpen('quotes')" class="dossier-content">
                <planning-detail-quote-view />
            </div>
        </div>

        <div v-if="showWorkOrders" class="dossier-section">
            <div class="dossier-header" @click="toggle('workOrders')">
                <md-icon>assignment</md-icon>
                <span class="dossier-title">Werkbonnen ({{ workOrderCount }})</span>
                <md-icon>{{ isOpen('workOrders') ? 'expand_less' : 'expand_more' }}</md-icon>
            </div>
            <div v-show="isOpen('workOrders')" class="dossier-content">
                <planning-detail-work-order-view />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import {
    IConfiguration,
    IPlanningItemEntity,
    IQuoteEntity,
    IWorkOrderEntity,
    PlanningItemType,
} from '@phrospr/api-backend';
import PlanningDetailAttachmentsView from '@/views/planning/planning-detail-tabs/PlanningDetailAttachmentsView.vue';
import PlanningDetailQuoteView from '@/views/planning/planning-detail-tabs/PlanningDetailQuoteView.vue';
import PlanningDetailWorkOrderView from '@/views/planning/planning-detail-tabs/PlanningDetailWorkOrderView.vue';

@Component({
    components: { PlanningDetailAttachmentsView, PlanningDetailQuoteView, PlanningDetailWorkOrderView },
})
export default class PlanningDetailDossierView extends Vue {
    // null = no manual toggle yet: sections with content show expanded, empty
    // ones collapsed — reactive, so sections open once their async data lands.
    manuallyToggled: { attachments: boolean | null; quotes: boolean | null; workOrders: boolean | null } = {
        attachments: null,
        quotes: null,
        workOrders: null,
    };

    isOpen(section: 'attachments' | 'quotes' | 'workOrders'): boolean {
        const manual = this.manuallyToggled[section];
        if (manual !== null) return manual;
        if (section === 'attachments') return this.attachmentCount > 0;
        if (section === 'quotes') return this.quoteCount > 0;
        return this.workOrderCount > 0;
    }

    get planningItem(): IPlanningItemEntity {
        return this.$store.state.planningItem;
    }

    get configuration(): Partial<IConfiguration> {
        return this.$store.state.configuration;
    }

    get quotes(): IQuoteEntity[] {
        return this.$store.state.quotes;
    }

    get workOrders(): IWorkOrderEntity[] {
        return this.$store.state.workOrders;
    }

    get attachmentCount(): number {
        return this.planningItem?.attachments ? this.planningItem.attachments.length : 0;
    }

    get quoteCount(): number {
        return this.quotes ? this.quotes.length : 0;
    }

    get workOrderCount(): number {
        return this.workOrders ? this.workOrders.length : 0;
    }

    get showQuotes(): boolean {
        return (
            this.planningItem.planning_item_type === PlanningItemType.lead ||
            this.planningItem.planning_item_type === PlanningItemType.placement
        );
    }

    get showWorkOrders(): boolean {
        return this.configuration?.workOrders?.enabled === true;
    }

    toggle(section: 'attachments' | 'quotes' | 'workOrders') {
        this.manuallyToggled[section] = !this.isOpen(section);
    }
}
</script>

<style scoped>
.dossier-section {
    border-bottom: 1px solid #e0e0e0;
    padding: 4px 0;
}

.dossier-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 4px;
    cursor: pointer;
    user-select: none;
}

.dossier-title {
    flex: 1;
    font-weight: 600;
}

.dossier-content {
    padding: 0 4px 8px 4px;
}
</style>
